export interface ParsedPlaceAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  /** Single-line address built from structured fields (no Plus Codes / business names). */
  formattedAddress: string;
  coordinates: { lat: number; lng: number };
}

type AddressComponent = google.maps.GeocoderAddressComponent;

function component(
  components: AddressComponent[],
  type: string,
  useShort = false,
): string {
  const match = components.find((c) => c.types.includes(type));
  if (!match) return '';
  return useShort ? match.short_name : match.long_name;
}

function buildFormattedAddress(parts: Omit<ParsedPlaceAddress, 'formattedAddress' | 'coordinates'>): string {
  const locality = [parts.city, parts.state].filter(Boolean).join(', ');
  const region = [parts.postalCode, parts.country].filter(Boolean).join(' ');
  return [parts.street, locality, region].filter(Boolean).join(', ');
}

/** Extract street, locality, region, country, and lat/lng from a Places result. */
export function parseGooglePlace(place: google.maps.places.PlaceResult): ParsedPlaceAddress | null {
  const location = place.geometry?.location;
  if (!location) return null;

  const components = place.address_components ?? [];
  const streetNumber = component(components, 'street_number');
  const route = component(components, 'route');
  const street =
    [streetNumber, route].filter(Boolean).join(' ') ||
    component(components, 'premise') ||
    component(components, 'subpremise') ||
    component(components, 'neighborhood');

  const city =
    component(components, 'locality') ||
    component(components, 'postal_town') ||
    component(components, 'administrative_area_level_2');

  const state = component(components, 'administrative_area_level_1');
  const postalCode = component(components, 'postal_code');
  const country = component(components, 'country');

  const parts = { street, city, state, postalCode, country };
  const formattedAddress = buildFormattedAddress(parts);

  return {
    ...parts,
    formattedAddress: formattedAddress || place.formatted_address || '',
    coordinates: { lat: location.lat(), lng: location.lng() },
  };
}
